"use client";

import Link from "next/link";
import Navigation from "../components/Navigation";
import RandomBackground from "../components/RandomBackground";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110 ${className}`}>
    {children}
  </div>
);

export default function Policy() {
  return (
    <div className="min-h-screen relative text-white">
      <Navigation />
      {/* Fixed background */}
      <RandomBackground />
      {/* Readability overlays */}
      <div
        aria-hidden
        className="fixed inset-0 -z-10 bg-gradient-to-b from-black/75 via-black/25 to-black/10"
      />
      <div
        aria-hidden
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(80% 60% at 50% 20%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 100%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 pb-20">
        {/* Policy Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Policy
              </h1>
              <p className="text-xl text-white/75 max-w-3xl mx-auto">
                By entering RainBlock.net you agree to accept the following terms
                and conditions. Please read carefully.
              </p>
            </div>

            <div className="space-y-8">
              {/* Trademarks & Patents */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Trademarks & Patents
                </h2>
                <div className="space-y-4 text-white/75 leading-relaxed">
                  <p>
                    Rain Block logos, graphics, images, text and all other content
                    are trademarks owned by Creative Home Inventions, LLC and can
                    not be used without prior written consent.
                  </p>
                  <p>
                    Patents are pending for certain Rain Block products. Any
                    infringement of these patents is prohibited.
                  </p>
                </div>
              </GlassCard>

              {/* Electric Communications */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Electric Communications
                </h2>
                <p className="text-white/75 leading-relaxed">
                  Anytime you send Rain Block an e-mail, or login onto our website
                  at RainBlock.net, you are communicating with us in an electric
                  format. This gives Rain Block the permission to communicate back
                  to you in the same electric format, either by e-mail or text
                  written on our website. With this, we both mutually agree that
                  any contracts, agreements, disclosures or notices, or any other
                  communication that is provided to you by Rain Block satisfies
                  all legal notification requirements.
                </p>
              </GlassCard>

              {/* Copyright */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Copyright</h2>
                <p className="text-white/75 leading-relaxed">
                  All content included on this site, such as text, graphics,
                  photos, logos, button icons, images, audio clips, digital
                  downloads, data compilations, are protected by United States and
                  International copyright laws. No portion of the information on
                  this website may be copied or reproduced in any form, or by any
                  means without prior written permission from Rain Block. Users or
                  visitors are not permitted to modify, distribute, publish,
                  transmit, copy or create derivative works of any material found
                  on this site or any Rain Block publication for any public,
                  private or commercial purpose.
                </p>
              </GlassCard>

              {/* Your Account */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Your Account
                </h2>
                <p className="text-white/75 leading-relaxed">
                  By creating and account and purchasing from this site, you agree
                  to accept responsibility for all actions that occur under your
                  account and password. Your accounts confidentiality and
                  passwords are strictly your responsibility. Red Dot Smoke
                  reserves the right to terminate any account, refuse sale, and/or
                  cancel orders for anyone or any account that is found out to be
                  under 18 or for any other reason we deem necessary.
                </p>
              </GlassCard>

              {/* Loss & Recovery in Shipping */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Loss & Recovery in Shipping
                </h2>
                <div className="space-y-4 text-white/75 leading-relaxed">
                  <p>
                    Once an item from Rain Block or one of it&apos;s affiliates
                    has been received by a shipping carrier, Rain Block is no
                    longer responsible for it&apos;s delivery to the customer. If
                    an item is lost by the carrier, or is not received from the
                    carrier, the remedy is between the purchaser and that shipping
                    carrier.
                  </p>
                  <p>
                    Creative Home Inventions, LLC welcomes international orders.
                    However, Creative Home Inventions, LLC will not be held liable
                    for items that are ordered and shipped to any country that
                    currently bans electronic cigarettes. If these items are
                    ordered and seized by customs or other agencies, the remedy is
                    between the purchaser and that agency.
                  </p>
                </div>
              </GlassCard>

              {/* Pricing */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Pricing</h2>
                <p className="text-white/75 leading-relaxed">
                  Rain Block reserves the right to change pricing on an item at
                  any time.
                </p>
              </GlassCard>

              {/* Disclaimer of Warranties and Limitation of Liability */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Disclaimer of Warranties and Limitation of Liability
                </h2>
                <div className="space-y-4 text-white/75 leading-relaxed">
                  <p>
                    RainBlock.net is provided by Creative Home Inventions, LLC on
                    an &quot;as is&quot; basis. The company makes no
                    representations or warranties of any kind, express or implied,
                    as to the operation of this site or the information, content,
                    materials, or products included on this site. You expressly
                    agree that your use of this site is at your sole risk.
                  </p>
                  <p>
                    To the full extent permissible by applicable law, Creative
                    Home Inventions, LLC disclaims all warranties, express or
                    implied, including, but not limited to, implied warranties of
                    merchantability and fitness for a particular purpose. Red Dot
                    Smoke does not warrant that this site, it&apos;s servers, or
                    emails sent from RainBlock.net are free of viruses or other
                    harmful components. Rain Block LLC will not be liable for any
                    damages of any kind arising from the use of this site,
                    including, but not limited to direct, indirect, incidental,
                    punitive, and consequential damages.
                  </p>
                </div>
              </GlassCard>

              {/* Applicable Law */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">
                  APPLICABLE LAW
                </h2>
                <p className="text-white/75 leading-relaxed">
                  By visiting this Site, you agree that the laws of the state of
                  Texas, without regard to principles of conflict of laws, will
                  govern these Conditions of Use and any dispute of any sort that
                  might arise between you and RainBlock.net or its affiliates.
                </p>
              </GlassCard>

              {/* Disputes */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">DISPUTES</h2>
                <p className="text-white/75 leading-relaxed">
                  Any dispute relating in any way to your visit to this Site or to
                  products you purchase through it shall be submitted to
                  confidential arbitration in Collin County Texas, except that, to
                  the extent you have in any manner violated or threatened to
                  violate Rain Block&apos;s intellectual property rights, Rain
                  Block&apos;s may seek injunctive or other appropriate relief in
                  any state or federal court in the state of Texas, and you
                  consent to exclusive jurisdiction and venue in such courts.
                  Arbitration under this agreement shall be conducted under the
                  rules then prevailing of the American Arbitration Association.
                  The arbitrator&apos;s award shall be binding and may be entered
                  as a judgment in any court of competent jurisdiction. To the
                  fullest extent permitted by applicable law, no arbitration under
                  this Agreement shall be joined to an arbitration involving any
                  other party subject to this Agreement, whether through class
                  arbitration proceedings or otherwise.
                </p>
              </GlassCard>

              {/* Product Return Policy */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Product Return Policy
                </h2>
                <div className="space-y-4 text-white/75 leading-relaxed">
                  <p>
                    We are committed to serving the needs of our customers. As
                    your satisfaction is extremely important to us, please first
                    carefully review the below outlined information, and then
                    contact our Customer Service Department based on these
                    instructions so that we may promptly address your needs.
                  </p>

                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      90 day Limited Warranty
                    </h3>
                    <p className="mb-4">
                      Creative Home Inventions, LLC, warrants only to the original
                      purchaser of this product from a Rain Block authorized
                      reseller or distributor that this product will be free from
                      defects in material and workmanship under normal use and
                      service for one year after date of purchase. This warranty
                      does not cover any defects or costs caused by:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        modification, alteration, repair or service of this
                        product by any persons or company other than Rain Block;
                      </li>
                      <li>
                        physical abuse to, or misuse of the product or operation
                        thereof in a manner inconsistent with the use indicated in
                        the instructions;
                      </li>
                      <li>
                        any use of the product other than that for which it was
                        intended; or
                      </li>
                      <li>shipment of the product to Rain Block for service.</li>
                    </ul>
                  </div>

                  <p>
                    Any express warranty not provided herein, and any remedy which
                    other than the warranty contained herein that might arise by
                    inference or operation of law, is hereby excluded and
                    disclaimed including the implied warranties of merchantability
                    and of the fitness for a particular purpose.
                  </p>

                  <p>
                    After 30 days, products cannot be returned for a refund.
                    Replacement or repair of product, therefore, is your exclusive
                    remedy under this warranty.
                  </p>

                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Return Process
                    </h3>
                    <p className="mb-4">
                      Subject to the above warranty, in the event that your Rain
                      Block device fails to operate satisfactorily, please simply
                      follow these 3 easy steps:
                    </p>
                    <ol className="list-decimal list-inside space-y-4 ml-4">
                      <li>
                        Email our Customer Service Department at
                        service@RainBlock.net in order to receive an approved,
                        Return Authorization Number (RAN#). Please label your
                        email subject line, &quot;Request for Return Authorization
                        Number.&quot; In the body of your email, please include
                        your (a) full contact information, including your first
                        and last name and/or company name, (b) order number,(c)
                        purchase date, (d) telephone, (e) fax, (f) email address,
                        (g) shipping address and (h) the reason for the return,
                        providing a detailed explanation of the difficulty you are
                        experiencing with the device and/or any of its components.
                        All of these information entries are required for
                        processing your request.
                      </li>
                      <li>
                        Within 48 hours of our receipt of your email, our Customer
                        Service Department will review the information you have
                        provided, and email you back with a Return Authorization
                        Number (RAN#). Please do not send any product to us unless
                        and until you have received your RAN#. Once you have
                        received your RAN#, please securely package your starter
                        kit and/or components, and include within your package a
                        copy of the email from our Customer Service Department
                        indicating your RAN#. If you are experiencing an issue
                        with a product component only, please do not send us your
                        entire starter kit. Mail any product returns to the
                        following address:
                        <div className="mt-2 ml-4">
                          Creative Home Inventions, LLC
                          <br />
                          451 Morgan Ln
                          <br />
                          Prosper, TX 75078
                        </div>
                      </li>
                      <li>
                        Once our Customer Service Department has received the
                        product you have returned, we will then send you a
                        confirmation email indicating our receipt of same, and our
                        email to you will include an estimated time of delivery of
                        your replacement Rain Block product and/or components.
                      </li>
                    </ol>
                  </div>

                  <p>
                    Please note that only packages with all contents are entitled
                    to a refund, less shipping fees incurred, provided that a
                    request for refund is made to our Customer Service Department,
                    along with shipment of product to the above indicated address
                    within 30 days from your purchase date.
                  </p>

                  <p>
                    If there is a discrepancy in your order -- please notify us
                    within a week of receiving your product, so we can rectify the
                    problem.
                  </p>
                </div>
              </GlassCard>

              {/* Shipping */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Shipping</h2>
                <div className="space-y-4 text-white/75 leading-relaxed">
                  <p>
                    Shipping is calculated automatically by the shopping cart
                    calculator. Shipping prices depend on the product weight and
                    the shipping location.
                  </p>
                  <p>
                    Free Shipping coupon codes apply to both domestic and
                    international orders.
                  </p>
                  <div className="mt-4">
                    <p className="text-white font-semibold">Rain Block,</p>
                    <p>Service Department</p>
                  </div>
                </div>
              </GlassCard>

              {/* Privacy Policy */}
              <GlassCard className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Privacy Policy
                </h2>
                <div className="space-y-4 text-white/75 leading-relaxed">
                  <p>
                    RainBlock.net is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you visit our website or make a purchase.
                  </p>
                  
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Information We Collect
                    </h3>
                    <p>
                      We collect information you provide directly to us, such as when you create an account, make a purchase, or contact us for support. This may include your name, email address, shipping address, phone number, and payment information.
                    </p>
                  </div>

                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      How We Use Your Information
                    </h3>
                    <p>
                      We use the information we collect to process your orders, communicate with you, improve our services, and comply with legal obligations. We do not sell or share your personal information with third parties for their marketing purposes.
                    </p>
                  </div>

                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Data Security
                    </h3>
                    <p>
                      We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
                    </p>
                  </div>

                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Your Rights
                    </h3>
                    <p>
                      You have the right to access, correct, or delete your personal information. To exercise these rights, please contact us at service@RainBlock.net.
                    </p>
                  </div>
                </div>
              </GlassCard>
            </div>

            {/* CTA Section */}
            <div className="text-center mt-16">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href="/"
                  className="bg-[#5fd0ff] text-gray-900 px-8 py-3 rounded-lg font-semibold hover:brightness-110 transition-all duration-300 transform hover:scale-105"
                >
                  Back to Home
                </Link>
                <Link
                  href="/faq"
                  className="border border-white/30 text-white px-8 py-3 rounded-lg font-semibold hover:border-white/50 hover:bg-white/5 transition-all duration-300"
                >
                  View FAQ
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-white/10 backdrop-blur-glass border-t border-white/15 py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-bold text-white">Rain Block<sup className="tm-reg">&reg;</sup></h3>
                <p className="text-white/75 text-sm mt-2">
                  When it rains, it shines.
                </p>
              </div>
              <div className="flex space-x-6">
                <Link
                  href="/policy"
                  className="text-white/75 hover:text-white transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  href="/policy"
                  className="text-white/75 hover:text-white transition-colors"
                >
                  Terms of Service
                </Link>
                <Link
                  href="/#contact"
                  className="text-white/75 hover:text-white transition-colors"
                >
                  Support
                </Link>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-white/15 text-center">
              <p className="text-white/75 text-sm">
                Â© 2024 Rain Block<sup className="tm-reg">&reg;</sup>. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}




